import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Home, 
  Users, 
  FileText, 
  BarChart2, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  QrCode,
  Shield
} from 'lucide-react';

const AdminLayout = () => {
  const { signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const navItems = [
    { to: "/admin", icon: <Home size={20} />, label: "Dashboard" },
    { to: "/admin/users", icon: <Users size={20} />, label: "Users" },
    { to: "/admin/forms", icon: <FileText size={20} />, label: "Forms" },
    { to: "/admin/analytics", icon: <BarChart2 size={20} />, label: "Analytics" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top navigation */}
      <header className="bg-gray-900 shadow-sm sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <div className="flex items-center">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-200 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div 
              className="flex items-center cursor-pointer"
              onClick={() => navigate('/admin')}
            >
              <div className="flex items-center">
                <QrCode size={28} className="text-blue-400" />
                <Shield size={16} className="text-red-400 -ml-2 -mt-4" />
                <span className="ml-2 text-xl font-bold text-white">Admin Panel</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center">
            {/* User dropdown */}
            <div className="ml-4 relative flex items-center">
              <div className="flex items-center">
                <div className="ml-3">
                  <div className="text-sm font-medium text-white">
                    Administrator
                  </div>
                  <button 
                    onClick={signOut}
                    className="text-xs text-gray-400 hover:text-gray-200 flex items-center"
                  >
                    <LogOut size={12} className="mr-1" />
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar for desktop */}
        <aside className="hidden md:flex md:flex-shrink-0">
          <div className="flex flex-col w-64 border-r border-gray-800 bg-gray-800">
            <div className="h-0 flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              {/* Navigation */}
              <nav className="mt-5 px-2 space-y-1">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`
                    }
                  >
                    <div className="mr-3">{item.icon}</div>
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>

            <div className="flex-shrink-0 flex border-t border-gray-700 p-4">
              <div className="flex-shrink-0 w-full group block">
                <div className="flex items-center">
                  <div>
                    <Settings size={20} className="text-gray-300" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-white">
                      System Settings
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile sidebar */}
        {sidebarOpen && (
          <div className="md:hidden fixed inset-0 z-40 flex">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setSidebarOpen(false)}
            ></div>

            {/* Sidebar */}
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gray-800">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="h-6 w-6 text-white" />
                </button>
              </div>
              
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex items-center px-4">
                  <QrCode size={28} className="text-blue-400" />
                  <Shield size={16} className="text-red-400 -ml-2 -mt-4" />
                  <span className="ml-2 text-xl font-bold text-white">Admin Panel</span>
                </div>
                
                <nav className="mt-5 px-2 space-y-1">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `group flex items-center px-2 py-2 text-base font-medium rounded-md ${
                          isActive
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`
                      }
                      onClick={() => setSidebarOpen(false)}
                    >
                      <div className="mr-4">{item.icon}</div>
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
              </div>
              
              <div className="flex-shrink-0 flex border-t border-gray-700 p-4">
                <div className="flex-shrink-0 w-full group block">
                  <div className="flex items-center">
                    <div>
                      <Settings size={20} className="text-gray-300" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-white">
                        System Settings
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;